import { getItem, RARITY_COLORS, type Rarity, TILE_SIZE } from '@shefira/shared';
import type Phaser from 'phaser';

import { HOVER_COLOR, hoverFrame } from './drop-hover.js';
import { hasBeam, lootBeam } from './loot-beam.js';
import { PixelText, SMALL_GLYPH_HEIGHT, SMALL_GLYPH_WIDTH } from './pixel-text.js';

/**
 * An item on the ground. Until item icons exist, a drop is a small diamond in
 * its rarity colour with the item's name above it in the compact font — enough
 * to see what fell and click it.
 */
export interface DropViewOptions {
  scene: Phaser.Scene;
  templateId: string;
  quantity: number;
  font: { atlasKey: string; fontKey: string };
  /** The item's icon in a packed atlas; without it the drop is a plain diamond. */
  icon?: { atlasKey: string; frame: string };
}

/** Diamond drawn as whole-pixel rows: widths 1, 3, 5, 3, 1. */
const DIAMOND_ROWS = [1, 3, 5, 3, 1];

/**
 * The « better than what you wear » arrow, the bag's (`ui/upgrade.css`) pixel
 * for pixel: ink outline then green, as [x, y, width] runs of one pixel high.
 */
const ARROW_INK: readonly (readonly [number, number, number])[] = [
  [3, 0, 1],
  [2, 1, 3],
  [1, 2, 5],
  [0, 3, 7],
  [1, 4, 5],
  [2, 5, 3],
  [2, 6, 3],
];
const ARROW_GREEN: readonly (readonly [number, number, number])[] = [
  [3, 1, 1],
  [2, 2, 3],
  [1, 3, 5],
  [3, 4, 1],
  [3, 5, 1],
];
const ARROW_INK_COLOR = 0x12121c;

/** Half a blink of a drop about to vanish: shown, then hidden, as long. */
const BLINK_MS = 250;
const ARROW_GREEN_COLOR = 0x9ece6a;

export class DropView {
  readonly container: Phaser.GameObjects.Container;
  readonly rarity: Rarity;
  private readonly label: PixelText;
  /** The column of light over a rare find (ADR 0049); none over a common one. */
  private readonly beam: Phaser.GameObjects.Graphics | undefined;
  /** How tall the drop draws, so the name clears it. */
  private readonly height: number;
  /** How wide, so the arrow sits on its top-right corner. */
  private readonly width: number;
  /** Drawn the first time the piece is better than the one worn, then shown or hidden. */
  private arrow: Phaser.GameObjects.Graphics | undefined;
  /** The rarity colour, which the name returns to when the mouse leaves. */
  private readonly color: number;
  /** The corner brackets, drawn the first time the mouse is over the drop. */
  private hoverMark: Phaser.GameObjects.Graphics | undefined;
  private hovered = false;

  constructor(options: DropViewOptions) {
    const { scene } = options;
    const template = getItem(options.templateId);
    this.rarity = template?.rarity ?? 'common';
    const color = RARITY_COLORS[this.rarity];
    this.color = color;
    this.container = scene.add.container(0, 0);
    if (hasBeam(this.rarity)) {
      this.beam = lootBeam(scene, this.rarity);
      this.container.add(this.beam);
    }

    if (options.icon !== undefined) {
      // The icon at half size: a 32 px inventory icon would swallow two tiles.
      const sprite = scene.add
        .image(0, 0, options.icon.atlasKey, options.icon.frame)
        .setOrigin(0.5, 1)
        .setScale(0.5);
      this.container.add(sprite);
      this.height = Math.round(sprite.displayHeight);
      this.width = Math.round(sprite.displayWidth);
    } else {
      DIAMOND_ROWS.forEach((width, row) => {
        const dot = scene.add
          .rectangle(-Math.floor(width / 2), row - DIAMOND_ROWS.length, width, 1, color)
          .setOrigin(0, 0);
        this.container.add(dot);
      });
      this.height = DIAMOND_ROWS.length;
      this.width = Math.max(...DIAMOND_ROWS);
    }

    this.label = new PixelText({
      scene,
      atlasKey: options.font.atlasKey,
      fontKey: options.font.fontKey,
      glyphWidth: SMALL_GLYPH_WIDTH,
      glyphHeight: SMALL_GLYPH_HEIGHT,
      color,
      outlineColor: 0x12121c,
    });
    const name = template?.name ?? options.templateId;
    this.label.setText(options.quantity > 1 ? `${name} x${String(options.quantity)}` : name);
    this.label.setPosition(0, -(this.height + SMALL_GLYPH_HEIGHT + 2));
    this.container.add(this.label.container);
  }

  /**
   * Centre of the tile, on whole pixels. Several drops on one tile fan out:
   * the diamonds step sideways and the names stack upward, so a rat's whole
   * table stays readable where it fell.
   */
  setTile(x: number, y: number, stackIndex = 0): void {
    const spread = ((stackIndex % 3) - 1) * 6;
    this.container.setPosition(
      x * TILE_SIZE + TILE_SIZE / 2 + spread,
      y * TILE_SIZE + TILE_SIZE - 2 - Math.floor(stackIndex / 3) * 2,
    );
    this.label.setPosition(
      -spread,
      -(this.height + SMALL_GLYPH_HEIGHT + 2 + stackIndex * (SMALL_GLYPH_HEIGHT + 1)),
    );
    // Ground items sit under anything standing on the same tile.
    this.container.setDepth(y * TILE_SIZE);
  }

  /** The green arrow over a piece better than the one worn (`isBetterThanWorn`). */
  setBetter(better: boolean): void {
    if (!better) {
      this.arrow?.setVisible(false);
      return;
    }
    if (this.arrow === undefined) {
      const arrow = this.container.scene.add.graphics();
      for (const [color, runs] of [
        [ARROW_INK_COLOR, ARROW_INK],
        [ARROW_GREEN_COLOR, ARROW_GREEN],
      ] as const) {
        arrow.fillStyle(color, 1);
        for (const [x, y, width] of runs) arrow.fillRect(x, y, width, 1);
      }
      // On the drawing's top-right corner, whole pixels: the icon is centred on x = 0.
      arrow.setPosition(Math.floor(this.width / 2) - 4, -this.height - 3);
      this.container.add(arrow);
      this.arrow = arrow;
    }
    this.arrow.setVisible(true);
  }

  /** Under the mouse (SHE-36): corner brackets round the drawing, the name in parchment. */
  setHovered(hovered: boolean): void {
    if (hovered === this.hovered) return;
    this.hovered = hovered;
    this.label.setColor(hovered ? HOVER_COLOR : this.color);
    if (!hovered) {
      this.hoverMark?.setVisible(false);
      return;
    }
    if (this.hoverMark === undefined) {
      const mark = this.container.scene.add.graphics();
      mark.fillStyle(HOVER_COLOR, 1);
      for (const [x, y, w, h] of hoverFrame(this.width, this.height)) mark.fillRect(x, y, w, h);
      this.container.add(mark);
      this.hoverMark = mark;
    }
    this.hoverMark.setVisible(true);
  }

  /**
   * A drop in its last seconds on the ground blinks, on and off: the server
   * says when (`fading`), the scene's clock says which half of the blink.
   */
  setFading(fading: boolean, time: number): void {
    this.container.setVisible(!fading || Math.floor(time / BLINK_MS) % 2 === 0);
  }

  destroy(): void {
    if (this.beam !== undefined) this.container.scene.tweens.killTweensOf(this.beam);
    this.container.destroy(true);
  }
}
